import { faEnvelope } from '@fortawesome/pro-regular-svg-icons'
import { Formik, FormikHelpers } from 'formik'
import React, { useState } from 'react'
import * as Yup from 'yup'
import { Alert, Content, Form, Input } from 'ui'
import { RequiredEmail } from 'validations'

export interface IForgotPasswordValues {
  [key: string]: string | undefined
  email?: string | undefined
}

const ForgotSchema = Yup.object().shape({
  email: RequiredEmail(),
})

const FormField = Input.Renderer<IForgotPasswordValues>()
const ErrorSummary = Form.ErrorSummaryRenderer<IForgotPasswordValues>()

/**
 * A simple forgot password form for existing users.
 */
export const ForgotPassword: React.FC = () => {
  const [complete, setComplete] = useState(false)

  const handleFormSubmit = async (
    { email }: IForgotPasswordValues,
    { setSubmitting, setErrors }: FormikHelpers<IForgotPasswordValues>
  ) => {
    setSubmitting(true)
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_EPC_API_URL ??
          process.env.REACT_APP_API_URL ??
          ''
        }/auth/forgot`,
        {
          body: JSON.stringify({ email }),
          method: 'POST',
        }
      )
      const { errors } = await response.json()
      if (errors && Object.keys(errors).length > 0) {
        if (errors.user) {
          setErrors({ Account: 'with the specified email does not exist.' })
        } else {
          setErrors(errors)
        }
      } else {
        setComplete(true)
      }
    } catch (e) {
      setErrors({ Server: 'could not be reached.' })
    }
    setSubmitting(false)
  }

  return (
    <Formik
      initialValues={
        {
          email: '',
        } as IForgotPasswordValues
      }
      onSubmit={handleFormSubmit}
      validationSchema={ForgotSchema}
    >
      {({ handleSubmit, isSubmitting, errors, submitCount, submitForm }) => {
        return complete ? (
          <Alert.Dismissable>
            <Content.Body marginClassNames="m-0">
              Please check your email.
            </Content.Body>
          </Alert.Dismissable>
        ) : (
          <Form.Form onSubmit={handleSubmit}>
            <Form.Row>
              <Form.Item width={[1]}>
                <FormField
                  disabled={isSubmitting}
                  icon={faEnvelope}
                  name="email"
                  label="Email Address"
                  type="text"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            {submitCount > 0 ? (
              <ErrorSummary errors={errors} />
            ) : (
              <ErrorSummary />
            )}
            <Form.Row>
              <Form.Item marginLeft="auto">
                <Content.ActionButton
                  onClick={submitForm}
                  loading={isSubmitting}
                  label="Send Reset Email"
                />
              </Form.Item>
            </Form.Row>
          </Form.Form>
        )
      }}
    </Formik>
  )
}
